import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../store/useStore'
import { generateRoadmap } from '../services/llmClient'
import { saveRoadmapToSupabase } from '../services/supabase'

const ONBOARDING_STEPS = [
  {
    id: 'topic',
    question: "What would you like to learn? 🎯",
    placeholder: "e.g., Data Analysis, Japanese, Web Development, Machine Learning...",
    type: 'text'
  },
  {
    id: 'currentLevel',
    question: "What's your current level? 📊",
    options: ['Complete Beginner', 'Some Knowledge', 'Intermediate', 'Advanced'],
    type: 'options'
  },
  {
    id: 'weeklyHours',
    question: "How many hours can you dedicate per week? ⏰",
    placeholder: "e.g., 5, 10, 20...",
    type: 'number'
  },
  {
    id: 'deadline',
    question: "How many weeks do you have? 📅",
    placeholder: "e.g., 8, 12, 24...",
    type: 'number'
  },
  {
    id: 'learningStyle',
    question: "What's your preferred learning style? 🎨",
    options: ['Hands-on Projects', 'Structured Courses', 'Balanced Mix'],
    type: 'options'
  },
  {
    id: 'preferences',
    question: "Any specific preferences? (Optional) 💡",
    placeholder: "e.g., Python over R, video tutorials, specific tools...",
    type: 'text',
    optional: true
  },
  {
    id: 'constraints',
    question: "Any constraints we should know? (Optional) ⚠️",
    placeholder: "e.g., budget limits, device limitations, internet speed...",
    type: 'text',
    optional: true
  }
]

const OnboardingChat = () => {
  const navigate = useNavigate()
  const { user, onboardingData, updateOnboardingData, resetOnboardingData, addRoadmap } = useStore()
  const [currentStep, setCurrentStep] = useState(0)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState(null)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    // Initial greeting
    setMessages([
      {
        type: 'bot',
        content: "Hi! I'm your PathPilot AI assistant. 👋\n\nI'll help you create a personalized learning roadmap tailored to your goals. Let's get started!",
        timestamp: new Date()
      },
      {
        type: 'bot',
        content: ONBOARDING_STEPS[0].question,
        timestamp: new Date()
      }
    ])
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSubmit = (value) => {
    const step = ONBOARDING_STEPS[currentStep]
    
    // Validate input
    if (!value && !step.optional) {
      setError('This field is required')
      return
    }

    setError(null)

    // Add user message
    setMessages(prev => [...prev, {
      type: 'user',
      content: value,
      timestamp: new Date()
    }])

    // Update onboarding data
    updateOnboardingData({ [step.id]: value })

    // Move to next step or generate roadmap
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: ONBOARDING_STEPS[currentStep + 1].question,
          timestamp: new Date()
        }])
        setCurrentStep(currentStep + 1)
        setInputValue('')
      }, 500)
    } else {
      // All steps completed, show confirmation
      setTimeout(() => {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: "Perfect! I have all the information I need. 🎉\n\nReady to generate your personalized learning roadmap?",
          timestamp: new Date()
        }])
      }, 500)
    }
  }

  const handleGenerateRoadmap = async () => {
    setIsGenerating(true)
    setError(null)

    try {
      setMessages(prev => [...prev, {
        type: 'bot',
        content: "Generating your roadmap... This may take a moment. ⚡",
        timestamp: new Date()
      }])

      const { roadmap, error: genError } = await generateRoadmap(onboardingData)

      if (genError || !roadmap) {
        throw new Error(genError || 'Failed to generate roadmap')
      }

      // Validate roadmap structure
      if (!roadmap.weeks || !Array.isArray(roadmap.weeks) || roadmap.weeks.length === 0) {
        throw new Error('Generated roadmap is missing weeks data')
      }

      // Ensure all weeks have tasks
      for (const week of roadmap.weeks) {
        if (!week.tasks) {
          week.tasks = []
        }
        if (!Array.isArray(week.tasks)) {
          week.tasks = []
        }
      }

      // Auto-save full roadmap to Supabase
      console.log('💾 Auto-saving roadmap to Supabase...')
      const { data: savedRoadmap, error: saveError } = await saveRoadmapToSupabase(roadmap, user.id)

      if (saveError) {
        console.error('❌ Error saving roadmap to Supabase:', saveError)
        // Show error but continue - roadmap is still in memory
        if (saveError.message && saveError.message.includes('Storage limit')) {
          setMessages(prev => [...prev, {
            type: 'bot',
            content: "⚠️ Note: " + saveError.message,
            timestamp: new Date()
          }])
        }
      } else {
        console.log('✅ Roadmap auto-saved to Supabase')
      }

      // Add to local store
      addRoadmap(savedRoadmap || roadmap)

      setMessages(prev => [...prev, {
        type: 'bot',
        content: "✅ Your roadmap is ready! Redirecting you now...",
        timestamp: new Date()
      }])

      setTimeout(() => {
        resetOnboardingData()
        navigate(`/roadmap/${roadmap.id}`)
      }, 1500)

    } catch (err) {
      console.error('Error generating roadmap:', err)
      const errorMsg = err.message || 'Failed to generate roadmap. Please try again.'
      setError(errorMsg)
      
      // Provide helpful error message based on error type
      let botMessage = "❌ Oops! Something went wrong."
      if (errorMsg.includes('Rate limit')) {
        botMessage = "⏳ We're experiencing high demand. Please wait 30 seconds and try again."
      } else if (errorMsg.includes('authentication')) {
        botMessage = "🔑 API authentication issue. Please contact support."
      } else {
        botMessage = "❌ Failed to generate roadmap. Please try again or adjust your inputs."
      }
      
      setMessages(prev => [...prev, {
        type: 'bot',
        content: botMessage,
        timestamp: new Date()
      }])
    } finally {
      setIsGenerating(false)
    }
  }

  const currentStepData = ONBOARDING_STEPS[currentStep]
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1
  const requiredSteps = ONBOARDING_STEPS.filter(s => !s.optional)
  const completedRequiredSteps = requiredSteps.filter(s => onboardingData[s.id]).length
  const allStepsCompleted = completedRequiredSteps === requiredSteps.length && currentStep === ONBOARDING_STEPS.length - 1

  return (
    <div className="gradient-bg flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Back to Dashboard</span>
            </button>

            <div className="flex items-center gap-3">
              <div className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                {currentStep + 1}/{ONBOARDING_STEPS.length}
              </div>
              <div className="w-40 h-2.5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  className="h-full bg-gradient-to-r from-violet-600 via-purple-500 to-cyan-500 rounded-full shadow-sm"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
              <span className="text-xs font-medium text-violet-600">
                {Math.round(((currentStep + 1) / ONBOARDING_STEPS.length) * 100)}%
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <AnimatePresence mode="popLayout">
            {messages.map((message, idx) => (
              <motion.div
                key={`message-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex gap-3 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' 
                      ? 'bg-gradient-to-br from-violet-600 to-cyan-500' 
                      : 'bg-gray-200'
                  }`}>
                    {message.type === 'user' ? (
                      <span className="text-white font-bold text-sm">
                        {user?.user_metadata?.full_name?.[0] || user?.email?.[0] || 'U'}
                      </span>
                    ) : (
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    )}
                  </div>

                  {/* Message */}
                  <div className={`rounded-2xl px-6 py-4 ${
                    message.type === 'user'
                      ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 text-white shadow-lg shadow-violet-500/30'
                      : 'bg-white shadow-lg border border-gray-100'
                  }`}>
                    <p className={`whitespace-pre-line text-base leading-relaxed ${message.type === 'user' ? 'font-medium' : ''}`}>
                      {message.content}
                    </p>
                    <p className={`text-xs mt-2 flex items-center gap-1 ${message.type === 'user' ? 'text-violet-100' : 'text-gray-400'}`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white/80 backdrop-blur-md border-t border-gray-200 px-4 py-6">
        <div className="max-w-3xl mx-auto">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
            >
              {error}
            </motion.div>
          )}

          {allStepsCompleted && !isGenerating ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex gap-4"
            >
              <button
                onClick={handleGenerateRoadmap}
                className="flex-1 btn-primary py-4 text-lg"
              >
                🚀 Generate My Roadmap
              </button>
              <button
                onClick={() => {
                  setCurrentStep(0)
                  setMessages([{
                    type: 'bot',
                    content: "Let's start over! " + ONBOARDING_STEPS[0].question,
                    timestamp: new Date()
                  }])
                  resetOnboardingData()
                }}
                className="btn-secondary"
              >
                Start Over
              </button>
            </motion.div>
          ) : isGenerating ? (
            <div className="flex items-center justify-center gap-3 py-4">
              <div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600 font-medium">Generating your roadmap...</span>
            </div>
          ) : currentStepData?.type === 'options' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {currentStepData.options.map((option, idx) => (
                <motion.button
                  key={option}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleSubmit(option)}
                  className="card p-5 text-left hover:border-violet-400 border-2 border-transparent transition-all group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <span className="font-semibold text-gray-900 relative z-10 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-100 to-blue-100 flex items-center justify-center text-violet-600 font-bold text-sm group-hover:scale-110 transition-transform">
                      {idx + 1}
                    </span>
                    {option}
                  </span>
                </motion.button>
              ))}
              {currentStepData.optional && (
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: currentStepData.options.length * 0.1 }}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleSubmit('')}
                  className="card p-5 text-left hover:border-gray-400 border-2 border-dashed border-gray-300 transition-all text-gray-500 hover:text-gray-700"
                >
                  <span className="font-medium flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Skip this step
                  </span>
                </motion.button>
              )}
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmit(inputValue)
              }}
              className="flex gap-3"
            >
              <div className="flex-1 relative">
                <input
                  type={currentStepData?.type === 'number' ? 'number' : 'text'}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={currentStepData?.placeholder}
                  className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-violet-500 focus:outline-none focus:ring-4 focus:ring-violet-100 transition-all text-lg shadow-sm"
                  autoFocus
                />
                {inputValue && (
                  <button
                    type="button"
                    onClick={() => setInputValue('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={!inputValue && !currentStepData?.optional}
                className="btn-primary px-8 py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
              >
                {currentStepData?.optional && !inputValue ? (
                  <>
                    <span>Skip</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span>Send</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default OnboardingChat
